// Notebooks — BOLUS_NOTES-18 (2026-09-17); meals, safe writes and read-only
// view BOLUS_NOTES-74..77, 79, 81, 83 (2026-10-06).
//
// The notebook is a journal: the server (list_journal) returns one ordered
// stream of bolus / basal / meal / note entries per profile + local day, plus a
// CGM summary. Pages are VIRTUAL — this page decides how many lines fit on one
// page for the current screen and lays consecutive days out across pages,
// so a busy day simply runs onto the next page ("continued…") like a paper
// notebook. Nothing about pages is stored.
//
// Writes follow BolusNotes/Docs/REQUIREMENTS_NOTEBOOK.md (see notebookWrites.ts):
// times are built in the PROFILE's timezone, new entries carry a client id,
// edits and deletes carry the version they were based on and a 409 shows the
// newer version, a read-only follower gets no add / edit / delete controls,
// and nothing is saved while offline.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { bolusnotesApi } from '../services/api';
import { subscribeSse } from '../services/sse';
import { getGlucoseColorClass, getTrendArrow, isReadingStale } from '../utils/glucoseDisplay';
import BolusEntryForm, { mealLabel } from '../components/NotebookForms/BolusEntryForm';
import BasalEntryForm from '../components/NotebookForms/BasalEntryForm';
import MealEntryForm from '../components/NotebookForms/MealEntryForm';
import NoteEntryForm from '../components/NotebookForms/NoteEntryForm';
import MonthPicker from '../components/NotebookForms/MonthPicker';
import './Notebooks.css';
import { layout } from './notebookLayout';
import type { Kind, JournalEntry, JournalDay, Journal, Profile, Line } from './notebookLayout';
import { buildDelete, buildSave, describeError, newClientId, nowIn, SaveError } from './notebookWrites';

const WINDOW_DAYS = 14;
const LINE_PX = 34;
const DEFAULT_TZ = 'America/New_York';

const ymd = (d: Date) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const addDays = (s: string, n: number) => { const d = new Date(s + 'T12:00:00'); d.setDate(d.getDate() + n); return ymd(d); };
const longDate = (s: string) => {
  const narrow = typeof window !== 'undefined' && window.innerWidth < 640;
  return new Date(s + 'T12:00:00').toLocaleDateString(undefined, narrow
    ? { weekday: 'short', month: 'short', day: 'numeric' }
    : { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const KIND_LABEL: Record<Kind, string> = { bolus: 'bolus', basal: 'basal', meal: 'meal', note: 'note' };
const cap = (s?: string) => (s ? s[0].toUpperCase() + s.slice(1) : '');

/** A one-line description of an entry, for the conflict panel. */
function summary(e: JournalEntry): string {
  if (e.kind === 'bolus') return `${e.local_time} · ${e.insulin_units}u${e.carbs_consumed ? ` · ${e.carbs_consumed}g` : ''}${e.blood_glucose_before ? ` · BG ${e.blood_glucose_before}` : ''}${e.food_description ? ` · ${e.food_description}` : ''}`;
  if (e.kind === 'basal') return `${e.local_time} · ${e.insulin_units}u ${e.insulin_type || ''}`;
  if (e.kind === 'meal') return `${e.local_time} · ${mealLabel(e)}`;
  return `${e.local_time} · ${e.text || ''}`;
}

interface FormState { kind: Kind; date: string; time: string; entry?: JournalEntry; clientId: string; photo?: string | null }
interface Conflict { data: Record<string, any>; fresh: JournalEntry | null }

/** WE-1: track the browser's connection. */
function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return online;
}

const Notebooks: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const [search, setSearch] = useSearchParams();
  const online = useOnline();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [profileId, setProfileId] = useState<string | undefined>(userId);
  const [journal, setJournal] = useState<Journal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState<number>(-1); // -1 = last page (today)
  const [capacity, setCapacity] = useState(18);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<SaveError | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [busy, setBusy] = useState(false);
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  const tz = journal?.profile?.timezone || DEFAULT_TZ;
  const canWrite = journal?.profile?.can_write !== false;   // PE-2: followers read only
  const today = journal?.today || nowIn(tz).date;

  // Window of days ending at `to` (default today).
  const to = search.get('to') || today;
  const from = addDays(to, -(WINDOW_DAYS - 1));

  // Profiles (for the picker + live CGM on today's page).
  const loadProfiles = useCallback(async () => {
    try {
      const data = await bolusnotesApi.dashboard.get();
      const list: Profile[] = data?.profiles || [];
      setProfiles(list);
      setProfileId((cur) => cur || list[0]?.id);
    } catch (e) { console.error('profiles', e); }
  }, []);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  // Live CGM: refetch dashboard data on every stream event (today's page shows it).
  useEffect(() => {
    const GATEWAY_URL = process.env.REACT_APP_GATEWAY_URL || 'https://nexus-dev.kstack.app';
    const stop = subscribeSse(`${GATEWAY_URL}/v1/app/bolus_notes/stream/glucose_update`, {
      onEvent: (name) => { if (name === 'reading') loadProfiles(); },
      onError: () => {},
    });
    const t = setInterval(loadProfiles, 120000);
    return () => { stop(); clearInterval(t); };
  }, [loadProfiles]);

  const loadJournal = useCallback(async (silent = false): Promise<Journal | null> => {
    if (!profileId) return null;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const j: Journal = await bolusnotesApi.journal.list(profileId, from, to);
      setJournal(j);
      return j;
    } catch (e: any) {
      setError(describeError(e).message || 'Could not load the notebook');
      return null;
    } finally { setLoading(false); }
  }, [profileId, from, to]);
  useEffect(() => { loadJournal(); }, [loadJournal]);

  // NB-7: which days have entries, for the date picker.
  const loadMarked = useCallback(async () => {
    if (!profileId) return;
    try {
      const r = await bolusnotesApi.journal.days(profileId, 366);
      setMarked(new Set((r?.dates || []).map((d: { date: string }) => d.date)));
    } catch { /* the picker still works without marks */ }
  }, [profileId]);
  useEffect(() => { loadMarked(); }, [loadMarked]);

  // Page capacity from the real height of the page body — this is what makes pages "virtual".
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setCapacity(Math.max(8, Math.floor(el.clientHeight / LINE_PX)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const pages = useMemo(() => (journal ? layout(journal.days, capacity) : []), [journal, capacity]);
  const current = pages.length ? pages[pageIndex < 0 ? pages.length - 1 : Math.min(pageIndex, pages.length - 1)] : null;
  const currentIdx = current ? current.index : 0;
  const profile = profiles.find((p) => p.id === profileId);
  const isToday = (d: string) => today === d;

  const goPage = (i: number) => {
    if (i < 0) { setSearch({ to: addDays(from, -1) }); setPageIndex(-1); return; }   // earlier window, land on its last page
    if (i >= pages.length) { if (to < today) { setSearch({ to: addDays(to, WINDOW_DAYS) }); setPageIndex(0); } return; }
    setPageIndex(i);
  };
  const jumpToDate = (d: string) => {
    if (d >= from && d <= to) {
      const i = pages.findIndex((p) => p.dates.includes(d));
      if (i >= 0) { setPageIndex(i); return; }
    }
    setSearch({ to: d }); setPageIndex(-1);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (form) return;
      if (e.key === 'ArrowLeft') goPage(currentIdx - 1);
      if (e.key === 'ArrowRight') goPage(currentIdx + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ── writes ─────────────────────────────────────────────────────────
  const findEntry = (j: Journal | null, e: JournalEntry) =>
    j?.days.flatMap((d) => d.entries).find((x) => x.id === e.id && x.kind === e.kind) || null;
  const dayMeals = (date: string) => (journal?.days.find((d) => d.date === date)?.entries || []).filter((e) => e.kind === 'meal');

  const openForm = (kind: Kind, date: string, entry?: JournalEntry) => {
    const time = entry ? entry.local_time : isToday(date) ? nowIn(tz).time : '12:00';   // NB-2: the profile's clock
    setFormError(null); setConflict(null);
    setForm({ kind, date, time, entry, clientId: newClientId() });   // EN-1: a retried save reuses this id
    // NO-2 / ME-5: show the existing sketch or photo while editing.
    if (entry?.has_photo && (kind === 'note' || kind === 'meal')) {
      (kind === 'meal' ? bolusnotesApi.journal.mealPhoto(entry.id) : bolusnotesApi.journal.notePhoto(entry.id))
        .then((r: any) => setForm((f) => (f && f.entry?.id === entry.id ? { ...f, photo: r?.photo_data || null } : f)))
        .catch(() => {});
    }
  };
  const closeForm = () => { setForm(null); setFormError(null); setConflict(null); };

  const afterWrite = async () => { await Promise.all([loadJournal(true), loadMarked()]); };

  const send = async (data: Record<string, any>, entry: JournalEntry | undefined, f: FormState) => {
    const call = buildSave(f.kind, data, { profileId: profileId!, tz, date: f.date, time: f.time, entry, entryDate: f.entry ? f.date : undefined, clientId: f.clientId });
    await bolusnotesApi.journal.call(call.action, call.payload);
  };

  const save = async (data: Record<string, any>) => {
    if (!form || !profileId) return;
    if (!online) { setFormError(describeError({ code: 'ERR_NETWORK' })); return; }   // WE-1
    setBusy(true); setFormError(null);
    try {
      await send(data, form.entry, form);
      closeForm();
      await afterWrite();
    } catch (e: any) {
      const err = describeError(e);
      if (err.conflict && form.entry) {
        // EN-5: someone else changed it — show theirs, offer to re-apply mine.
        const j = await loadJournal(true);
        setConflict({ data, fresh: findEntry(j, form.entry) });
      } else setFormError(err);
    } finally { setBusy(false); }
  };

  const resolveConflict = async (choice: 'mine' | 'theirs') => {
    if (!form || !conflict) return;
    if (choice === 'theirs') { closeForm(); return; }
    setBusy(true); setFormError(null);
    try {
      // Re-apply on top of the newer version; if it was deleted meanwhile, save mine as a new entry.
      if (conflict.fresh) await send(conflict.data, conflict.fresh, form);
      else await send(conflict.data, undefined, { ...form, entry: undefined });
      closeForm();
      await afterWrite();
    } catch (e: any) {
      const err = describeError(e);
      if (err.conflict) { const j = await loadJournal(true); setConflict({ data: conflict.data, fresh: form.entry ? findEntry(j, form.entry) : null }); }
      else setFormError(err);
    } finally { setBusy(false); }
  };

  const remove = async (e: JournalEntry) => {
    if (!online) { alert('No connection — nothing was deleted. Try again when you are back online.'); return; }
    if (!window.confirm(`Delete this ${KIND_LABEL[e.kind]} entry?`)) return;
    try {
      const call = buildDelete(e);
      await bolusnotesApi.journal.call(call.action, call.payload);
      await afterWrite();
    } catch (err: any) {
      const d = describeError(err);
      if (d.conflict) {
        await loadJournal(true);
        alert('This entry was changed on another device, so it was not deleted. The newer version is shown now — delete it again if you still want to.');
      } else alert(d.message || 'Delete failed');
    }
  };

  // ── render ─────────────────────────────────────────────────────────
  const renderEntry = (e: JournalEntry, day: JournalDay) => {
    const meal = e.kind === 'bolus' && e.meal_id ? day.entries.find((m) => m.id === e.meal_id) : undefined;
    return (
      <div className={`nb-entry nb-${e.kind}`} key={e.id}>
        <span className="nb-time">{e.local_time}</span>
        <span className="nb-kind">{e.kind === 'bolus' ? 'Bolus' : e.kind === 'basal' ? `Basal · ${e.insulin_type || 'Lantus'}` : e.kind === 'meal' ? cap(e.meal_type || 'meal') : 'Note'}</span>
        <span className="nb-body">
          {e.kind === 'bolus' && (<>
            <b>{e.insulin_units}u</b>
            {e.carbs_consumed ? <span> · {e.carbs_consumed}g carbs</span> : null}
            {e.blood_glucose_before ? <span> · BG {e.blood_glucose_before}</span> : null}
            {e.meal_type && e.meal_type !== 'bolus' ? <span> · {e.meal_type}</span> : null}
            {e.injection_site ? <span> · {e.injection_site}</span> : null}
            {e.meal_id ? <span className="nb-link" title={meal ? mealLabel(meal) : 'Linked to a meal'}> · 🍽 {meal?.description || 'meal'}</span> : null}
            {e.food_description ? <div className="nb-sub">{e.food_description}</div> : null}
          </>)}
          {e.kind === 'basal' && (<><b>{e.insulin_units}u</b>{e.injection_site ? <span> · {e.injection_site}</span> : null}</>)}
          {e.kind === 'meal' && (<span>{e.description || <i>meal</i>}{e.carbs != null ? <b> · {e.carbs}g</b> : null}{e.has_photo ? ' 📷' : ''}</span>)}
          {e.kind === 'note' && (<span className="nb-note-text">{e.text}{e.has_photo ? ' ✏️' : ''}</span>)}
        </span>
        {canWrite && (
          <span className="nb-actions">
            <button title="Edit" aria-label={`Edit ${KIND_LABEL[e.kind]}`} onClick={() => openForm(e.kind, day.date, e)}>✎</button>
            <button title="Delete" aria-label={`Delete ${KIND_LABEL[e.kind]}`} onClick={() => remove(e)}>🗑</button>
          </span>
        )}
      </div>
    );
  };

  const renderLine = (l: Line) => {
    switch (l.kind) {
      case 'day': {
        const t = l.day.totals;
        const parts = [t.bolus_units > 0 && `${t.bolus_units}u bolus`, t.basal_units > 0 && `${t.basal_units}u basal`, t.carbs > 0 && `${t.carbs}g carbs`].filter(Boolean);
        return (
          <div className={`nb-day-header ${isToday(l.day.date) ? 'today' : ''}`} key={l.key}>
            <div className="nb-day-title">{longDate(l.day.date)}{isToday(l.day.date) && <span className="nb-today-chip">Today</span>}</div>
            <div className="nb-day-tools">
              {parts.length > 0 && <span className="nb-totals">{parts.join(' · ')}</span>}
              {canWrite && (<>
                <button disabled={!online} onClick={() => openForm('bolus', l.day.date)}>+ Bolus</button>
                <button disabled={!online} onClick={() => openForm('meal', l.day.date)}>+ Meal</button>
                <button disabled={!online} onClick={() => openForm('basal', l.day.date)}>+ Basal</button>
                <button disabled={!online} onClick={() => openForm('note', l.day.date)}>+ Note</button>
              </>)}
            </div>
          </div>);
      }
      case 'cgm': {
        const c = l.day.cgm!; const live = isToday(l.day.date) ? profile?.latest_reading : null;
        return (
          <div className="nb-cgm" key={l.key}>
            {live?.glucose_value != null && (
              <span className={`nb-live ${isReadingStale(Date.now(), live.timestamp) ? 'stale' : getGlucoseColorClass(live.glucose_value)}`}
                    title={isReadingStale(Date.now(), live.timestamp) ? 'Stale — no reading for over 10 minutes' : undefined}>{live.glucose_value} {getTrendArrow(live.trend)} <small>{isReadingStale(Date.now(), live.timestamp) ? 'stale' : 'now'}</small></span>
            )}
            <span>CGM · avg {c.avg} · {c.min}–{c.max} · in range {c.in_range_pct}% · low {c.low_pct}% · high {c.high_pct}%</span>
          </div>);
      }
      case 'drawing': return (
        <div className="nb-drawing" key={l.key} title="Hand drawing from the iPad/iPhone notebook">
          ✍️ Drawing{l.day.drawing?.extracted_text ? <span> — “{l.day.drawing.extracted_text}”</span> : <span> (open on iPad/iPhone to view)</span>}
        </div>);
      case 'continued': return <div className="nb-continued" key={l.key}>{longDate(l.day.date)} — continued</div>;
      case 'empty': return <div className="nb-empty" key={l.key}>No entries.</div>;
      case 'entry': return renderEntry(l.entry!, l.day);
      default: return null;
    }
  };

  const formProps = form ? { initialData: form.entry, onCancel: closeForm, onSave: save } : null;

  return (
    <div className="ios-dashboard-container notebooks-page">
      <header className="ios-header">
        <div className="header-left">
          <button onClick={() => navigate('/dashboard')} className="icon-btn">←</button>
          <h1>Notebook</h1>
          {journal && !canWrite && <span className="nb-readonly" title="You follow this profile; only the family can change it">View only</span>}
        </div>
        <div className="header-actions">
          {profiles.length > 1 && (
            <select className="nb-profile" value={profileId || ''} onChange={(e) => { setProfileId(e.target.value); setPageIndex(-1); navigate(`/notebooks/${e.target.value}`); }}>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <ThemeToggle placement="header" />
        </div>
      </header>

      <main className="ios-content nb-main">
        {!online && <div className="nb-offline" role="status">You're offline — the notebook can't be changed until the connection is back.</div>}
        <div className="nb-nav">
          <button className="icon-btn" onClick={() => goPage(currentIdx - 1)} title="Previous page (←)">‹</button>
          <MonthPicker value={current?.dates[0] || to} today={today} marked={marked} onPick={jumpToDate} />
          <button className="nb-today-btn" onClick={() => { setSearch({}); setPageIndex(-1); }}>Today</button>
          <span className="nb-pageno">{pages.length ? `Page ${currentIdx + 1} of ${pages.length}` : ''}</span>
          <button className="icon-btn" onClick={() => goPage(currentIdx + 1)} title="Next page (→)" disabled={to >= today && currentIdx >= pages.length - 1}>›</button>
        </div>

        <div className={`nb-page ${current?.continuedFrom ? 'cont-from' : ''}`}>
          <div className="nb-page-body" ref={bodyRef}>
            {loading ? <div className="loading-spinner" style={{ margin: '2rem auto' }}>Loading…</div>
              : error ? <div className="nb-error">{error}</div>
              : !profileId ? <div className="nb-empty">No profiles yet.</div>
              : current ? current.lines.map(renderLine) : null}
          </div>
          {current?.continuedOn && <div className="nb-continued-on">continued on next page →</div>}
        </div>
      </main>

      {form && formProps && (
        <div className="modal-overlay nb-modal">
          <div className="nb-modal-card">
            <div className="nb-modal-head">
              <span>{longDate(form.date)}</span>
              <label>Time <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></label>
            </div>
            {tz !== Intl.DateTimeFormat().resolvedOptions().timeZone && <div className="nb-tz">Times are {profile?.name || 'the profile'}'s local time ({tz.replace('_', ' ')}).</div>}
            {conflict ? (
              <div className="nb-conflict" role="alert">
                {conflict.fresh ? (<>
                  <p><b>This entry was changed on another device</b> while you were editing it. The newer version is:</p>
                  <p className="nb-conflict-theirs">{summary(conflict.fresh)}</p>
                  <div className="form-actions" style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-secondary" onClick={() => resolveConflict('theirs')} disabled={busy}>Keep the newer version</button>
                    <button className="btn-primary" onClick={() => resolveConflict('mine')} disabled={busy}>Save my change over it</button>
                  </div>
                </>) : (<>
                  <p><b>This entry was deleted on another device</b> while you were editing it.</p>
                  <div className="form-actions" style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-secondary" onClick={() => resolveConflict('theirs')} disabled={busy}>Leave it deleted</button>
                    <button className="btn-primary" onClick={() => resolveConflict('mine')} disabled={busy}>Save mine as a new entry</button>
                  </div>
                </>)}
              </div>
            ) : (<>
              {form.kind === 'bolus' && <BolusEntryForm {...formProps} meals={dayMeals(form.date)} />}
              {form.kind === 'basal' && <BasalEntryForm {...formProps} />}
              {form.kind === 'meal' && <MealEntryForm {...formProps} photoData={form.photo} />}
              {form.kind === 'note' && <NoteEntryForm {...formProps} photoData={form.photo} />}
            </>)}
            {formError && <div className="nb-form-error" role="alert">{formError.message}</div>}
            {busy && <div className="nb-busy">Saving…</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notebooks;
